import * as angular from 'angular';
import { Component, Inject, Input, OnInit, AfterViewInit } from 'ng-metadata/core';
import './find.component.scss';

/**
 * @ngdoc directive
 * @name mwFind
 * @module mw.components.find
 *
 * @param {string} [search=''] - Property bound to search input value.
 * @param {boolean} [clear-on-menu-close='false'] - Wether to add $mdMenuClose event to clear input.
 * @param {boolean} [autofocus='false'] - Wether to autofocus the find input.
 * @param {number} [debounce='0'] - Time in ms to debounce model changes.
 * @param {string} [placeholder='Search'] Text for the placeholder component to be passed in. Default text is translated.
 * @param {number} [min-length='1'] Amount of characters needed to type before filtering.
 *
 * @description
 * `<mw-find>` is find component which allows to include md-autocomplete.
 *
 * @usage
 * <hljs hljs-language="html">
 *     <mw-find search="$ctrl.searchModel">
 *          <md-autocomplete>
 *              <md-item-template>
 *                   <span md-highlight-text="$ctrl.searchModel" md-highlight-flags="^i">{{item.label}}</span>
 *               </md-item-template>
 *               <md-not-found>
 *                   No results
 *               </md-not-found>
 *           </md-autocomplete>
 *     </mw-find>
 * </hljs>
 *
 */

@Component({
    selector: 'mw-find',
    template: `
        <div class="mw-find" layout="row" md-autocomplete-snap="width" ng-class="{ 'mw-find--has-focus': $ctrl.hasFocus, 'mw-find--has-value': $ctrl.search, 'mw-find--autocomplete': $ctrl.hasAutocomplete(), 'mw-find--filter': !$ctrl.hasAutocomplete() }">
            <mw-icon class="mw-find__search-icon" svg-icon="action:ic_search_24px"></mw-icon>
            <div class="mw-find__input-container" ng-if="!$ctrl.hasAutocomplete()" flex="100">
                <input class="mw-find__input" type="text" placeholder="{{ $ctrl.placeholder }}" ng-model="$ctrl.search" ng-model-options="{debounce: $ctrl.debounce}" ng-minlength="$ctrl.minLength" md-autofocus="::$ctrl.autofocus" ng-focus="$ctrl.toggleFocus()" ng-blur="$ctrl.toggleFocus()" />
            </div>
            <div class="mw-find__autocomplete-container" ng-transclude="findAutocomplete" ng-show="$ctrl.hasAutocomplete()" flex="100"></div>
            <mw-icon-button class="mw-find__cancel-icon-button" ng-class="{ 'mw-find__cancel-icon-button--visible' : $ctrl.search && $ctrl.search.length > 0 }" svg-icon="navigation:ic_cancel_24px" size="small" ng-click="$ctrl.clearFind()" hover="true" md-prevent-menu-close="true"></mw-icon-button>
            <span ng-if="::!$ctrl.autofocus" md-autofocus></span>
        </div>
    `,
    legacy: {
        transclude: {
            'findAutocomplete': '?mdAutocomplete'
        }
    }
})
export class FindComponent implements OnInit, AfterViewInit {
    @Input('=') search: string = '';
    @Input('@') debounce: number = 0;
    @Input('<') clearOnMenuClose: boolean = false;
    @Input('<') autofocus: boolean = false;
    @Input('@') placeholder: string;
    @Input('@') minLength: number = 1;
    hasFocus: boolean = false;

    constructor(@Inject('$scope') private $scope: angular.IScope,
                @Inject('$timeout') private $timeout: angular.ITimeoutService,
                @Inject('$transclude') private $transclude: angular.ITranscludeFunction,
                @Inject('$translate') private $translate,
                @Inject('$element') private $element,
                @Inject('$mdUtil') private $mdUtil) {
        'ngInject';
    }

    ngOnInit() {
        if (this.clearOnMenuClose) {
            this.$scope.$root.$on('$mdMenuClose', () => {
                // Trick to prevent seeing clear while md-menu is still animating close.
                this.$timeout(this.clearFind.bind(this), 800);
            });
        }

        if (!this.placeholder) {
            this.placeholder = this.$translate.instant('mw-collection-search__find');
        }
    }

    ngAfterViewInit() {
        if (this.hasAutocomplete()) {
            this.$mdUtil.nextTick(() => {
                // transcluded content like the md-autocomplete is not imediatly accessible via DOM, using
                // nextTick to ensure the md-autocomplete has rendered so that we can attach DOM event handlers
                let autoCompleteInput = this.$element.find('input');
                if (autoCompleteInput && autoCompleteInput[0]) {
                    autoCompleteInput.on('focus blur', () => { this.toggleFocus(true) });
                }
            });
        }
    }

    clearFind() {
        this.search = '';
    }

    hasAutocomplete() {
        return this.$transclude.isSlotFilled('findAutocomplete')
    }

    toggleFocus(forceDigest) {
        this.hasFocus = !this.hasFocus;
        if (forceDigest) {
            // see https://stackoverflow.com/questions/18626039/apply-already-in-progress-error
            if (this.$scope.$root.$$phase !== '$apply' && this.$scope.$root.$$phase !== '$digest') {
                this.$scope.$apply();
            }
        }
    }

}



// WEBPACK FOOTER //
// ./src/lib/components/find/find.component.ts